import React, { useImperativeHandle, forwardRef, useRef, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Empty, Tabs, message } from 'antd';
import styles from './index.module.scss';
import API from '@api/api';
import { AppContext } from '@utils/AppContext';
import { BATCH_IMAGES_TYPE, IMAGE_TYPE, TYPETITLEMAP } from '@utils/CONST';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-awesome-infinite-scroll';
import Utils from '@utils/utils';
import GoBack from '../goBack/goBack';
import { downloadFlowOutputImages, getFlowProjectHistoryImages, getFlowProjectPage } from '../../../FreeCanvas/execution';

const TAB_KEY = {
    MODEL: 'model',
    CREATIVE: IMAGE_TYPE.CREATIVE_IMAGE_GEN,
    TOOLBOX: 'aiEdit',
    ENTERPRISE: 'bachImage',
};
const PAGE_SIZE = 18;
const BATCH_PROJECT_TYPE_TITLE_MAP = {
    [BATCH_IMAGES_TYPE.TRYON_BATCH]: 'AI试衣',
    [BATCH_IMAGES_TYPE.CHANGE_MODEL_BATCH]: '换模特',
    [BATCH_IMAGES_TYPE.CHANGE_BKG_BATCH]: '换背景',
    [BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH]: '同款换色',
    [BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH]: 'AI超清',
    [BATCH_IMAGES_TYPE.WATERMARK_REMOVE_BATCH]: '去水印',
    [BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH]: '搭配上身',
    [BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH]: '种草图',
    [BATCH_IMAGES_TYPE.SMART_DETAIL_PAGE_BATCH]: '服装详情页',
    [BATCH_IMAGES_TYPE.IMAGE_TO_VIDEO_BATCH]: '图生视频',
};

const getProjectCover = (project, tabKey) => {
    if (tabKey === TAB_KEY.ENTERPRISE) return project?.skuImageList?.[0] || project?.mainImage || '';
    if (tabKey === TAB_KEY.CREATIVE) {
        const cover = Array.isArray(project?.cover) ? project.cover[0] : project?.cover;
        return cover || project?.mainImage || '';
    }
    return project?.mainImage?.split(',')?.[0] || '';
};

const getSelectionKey = item => (
    `${item.source}:${item.projectId || ''}:${item.taskId || ''}:${item.outputId || ''}:${item.url || ''}`
);

const ProjectItem = (props) => {
    const cover = getProjectCover(props, props.tabKey);
    const canOpen = Boolean(cover || props.tabKey === TAB_KEY.CREATIVE);
    const projectName = props.projectName || props.name || '';
    const typeTitle = props.typeTitle || (
        props.tabKey === TAB_KEY.ENTERPRISE
            ? (BATCH_PROJECT_TYPE_TITLE_MAP[props.projectType] || '')
            : (props.tabKey === TAB_KEY.MODEL || props.tabKey === TAB_KEY.TOOLBOX)
                ? (TYPETITLEMAP[props.imageType] || '')
                : ''
    );
    const displayProjectName = typeTitle ? `${typeTitle} | ${projectName}` : projectName;

    return (
        <div className={styles.ProjectItem}>
            <div className={`${styles.ProjectItemCover} ${props.favorite && styles.favoriteBox}`}>
                {cover ? <img src={Utils.getImgUrlWithWebp(cover, true)} alt="" /> : props.favorite ? <div className={styles.empty_img}></div> : null}
                {canOpen ? (
                    <div className={styles.mask} onClick={props.onClick}>
                        <div className={styles.check_view}>查看合集</div>
                    </div>
                ) : null}
            </div>
            {props.favorite ? (
                <>
                    <p className={styles.ProjectName}><i className={styles.favor_icon}></i><span>{props.projectName}</span></p>
                    <p className={styles.UpdatedTime}></p>
                </>
            ) : (
                <>
                    <p className={styles.ProjectName} title={displayProjectName}>
                        <span className={styles.ProjectNameText}>{displayProjectName}</span>
                    </p>
                    <p className={styles.UpdatedTime}>修改时间 {dayjs(props.updatedTime).format('YYYY/MM/DD HH:mm')}</p>
                </>
            )}
        </div>
    );
};

const TaskItem = props => (
    <div
        className={`${styles.TaskItem} ${props.actived && styles.actived}`}
        onDoubleClick={props.onDoubleClick}
        onClick={props.onClick}
    >
        <img src={Utils.getImgUrlWithWebp(props.url, true)} alt="" />
    </div>
);

const normalizeStandardTasks = (tasks, source, projectId) => (
    (Array.isArray(tasks) ? tasks : [])
        .filter(item => item?.status === 'succeeded' && item?.results?.length > 0)
        .map(item => ({ source, projectId, taskId: item.taskId, url: item.results[0]?.image }))
        .filter(item => item.url)
);

const normalizeEnterpriseTasks = (tasks, projectId) => (
    (Array.isArray(tasks) ? tasks : []).flatMap(task => {
        if (task?.status !== 'succeeded') return [];
        return (Array.isArray(task?.results) ? task.results : []).map((result, index) => ({
            source: TAB_KEY.ENTERPRISE,
            projectId,
            taskId: task.taskId,
            outputId: result?.id || index,
            url: result?.image,
        }));
    }).filter(item => item.url)
);

const normalizeCreativeTasks = (tasks, projectId) => (
    (Array.isArray(tasks) ? tasks : []).map(item => ({
        source: TAB_KEY.CREATIVE,
        projectId,
        taskId: item?.taskId,
        outputId: item?.outputId || item?.id,
        url: item?.url,
    })).filter(item => item.url)
);

const TaskList = ({ tabKey, projectId, member, multiple, selectedTasks, creativeTasks, onToggleTask, onDoubleClick }) => {
    const [taskList, setTaskList] = useState([]);
    const { globalUser } = useContext(AppContext);

    useEffect(() => {
        let cancelled = false;
        const loadTasks = async () => {
            try {
                let nextTasks = [];
                if (tabKey === TAB_KEY.CREATIVE) {
                    nextTasks = Array.isArray(creativeTasks) ? creativeTasks : [];
                } else if (tabKey === TAB_KEY.ENTERPRISE) {
                    const res = await API.batchListResult({ projectId });
                    if (res?.code !== 0) throw new Error(res?.message);
                    nextTasks = normalizeEnterpriseTasks(res?.data, projectId);
                } else if (projectId === 'favorite') {
                    const res = await API.listBookmarkTaskv2({
                        module: tabKey,
                        member: member === 'team' ? null : member || globalUser?.id,
                    });
                    if (res?.code !== 0) throw new Error(res?.message);
                    nextTasks = normalizeStandardTasks(res?.data, tabKey, projectId);
                } else {
                    const res = await API.getProjectDetailv2({
                        projectId,
                        member: member === 'team' ? null : member || globalUser?.id,
                    });
                    if (res?.code !== 0) throw new Error(res?.message);
                    nextTasks = normalizeStandardTasks(res?.data?.taskList, tabKey, projectId).reverse();
                }
                if (!cancelled) setTaskList(nextTasks);
            } catch (err) {
                if (!cancelled) {
                    setTaskList([]);
                    message.warning(err?.message || '加载项目图片失败');
                }
            }
        };
        loadTasks();
        return () => { cancelled = true; };
    }, [creativeTasks, globalUser?.id, member, projectId, tabKey]);

    const handleDoubleClick = async (item) => {
        if (multiple) return;
        try {
            let url = item.url;
            if (item.source === TAB_KEY.CREATIVE && item.outputId) {
                const urls = await downloadFlowOutputImages(item.projectId, [item.outputId]);
                url = urls[0] || url;
            } else if (item.source !== TAB_KEY.ENTERPRISE && item.taskId) {
                const res = await API.commonDownload({ taskId: item.taskId });
                if (res?.code !== 0) throw new Error(res?.message);
                url = res.data || url;
            }
            onDoubleClick?.(url);
        } catch (err) {
            message.warning(err?.message || '获取图片失败');
        }
    };

    return (
        <div className={`${styles.ListContent2} _beauty_scrollbar`}>
            {taskList.map(item => {
                const selectionKey = getSelectionKey(item);
                return (
                    <TaskItem
                        key={selectionKey}
                        {...item}
                        actived={selectedTasks.some(selected => getSelectionKey(selected) === selectionKey)}
                        onDoubleClick={() => handleDoubleClick(item)}
                        onClick={() => onToggleTask(item)}
                    />
                );
            })}
        </div>
    );
};

const PickFromProject = forwardRef((props, ref) => {
    const [tabKey, setTabKey] = useState(TAB_KEY.MODEL);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [projectId, setProjectId] = useState();
    const [projectName, setProjectName] = useState();
    const [projectList, setProjectList] = useState([]);
    const pageIndexRef = useRef(1);
    const [hasMore, setHasMore] = useState(false);
    const [favoriteList, setFavoriteList] = useState([]);
    const [creativeTasks, setCreativeTasks] = useState([]);
    const creativeRequestIdRef = useRef(0);
    const { member, globalUser, globalOrgInfo } = useContext(AppContext);
    const showEnterprise = Boolean(globalOrgInfo && !globalOrgInfo.isPersonal);

    const tabItems = useMemo(() => [
        { key: TAB_KEY.MODEL, label: 'AI商拍图' },
        { key: TAB_KEY.CREATIVE, label: 'AI创意设计' },
        { key: TAB_KEY.TOOLBOX, label: 'AI工具箱' },
        ...(showEnterprise ? [{ key: TAB_KEY.ENTERPRISE, label: '企业版' }] : []),
    ], [showEnterprise]);

    const getMember = useCallback(
        () => (member === 'team' ? null : member || globalUser?.id),
        [globalUser?.id, member],
    );

    const fetchProjects = useCallback(async (pageIndex) => {
        if (tabKey === TAB_KEY.CREATIVE) {
            return getFlowProjectPage({ pageIndex, pageSize: PAGE_SIZE, member: getMember() });
        }
        if (tabKey === TAB_KEY.ENTERPRISE) {
            const res = await API.listBatchProject({
                pageIndex,
                pageSize: PAGE_SIZE,
                myProjectData: true,
                member: getMember(),
            });
            if (res?.code !== 0) throw new Error(res?.message);
            return res.data;
        }
        const res = await API.listProjectv2({
            module: tabKey,
            pageIndex,
            pageSize: PAGE_SIZE,
            member: getMember(),
        });
        if (res?.code !== 0) throw new Error(res?.message);
        return {
            ...res.data,
            items: res.data.items.filter(item => item.imageType !== IMAGE_TYPE.MODEL_VIDEO),
        };
    }, [getMember, tabKey]);

    const onSubmit = async () => {
        if (!selectedTasks.length) return false;
        try {
            const urls = await Promise.all(selectedTasks.map(async item => {
                if (item.source === TAB_KEY.CREATIVE) {
                    if (!item.outputId) return item.url;
                    const result = await downloadFlowOutputImages(item.projectId, [item.outputId]);
                    return result[0] || item.url;
                }
                if (item.source === TAB_KEY.ENTERPRISE) return item.url;
                const res = await API.commonDownload({ taskId: item.taskId });
                if (res?.code !== 0) throw new Error(res?.message);
                return res.data || item.url;
            }));
            const filteredUrls = urls.filter(Boolean);
            return props.multiple ? filteredUrls : filteredUrls[0] || false;
        } catch (err) {
            message.warning(err?.message || '获取图片失败');
            return false;
        }
    };

    useImperativeHandle(ref, () => ({ onSubmit }));

    useEffect(() => {
        let cancelled = false;
        pageIndexRef.current = 1;
        creativeRequestIdRef.current += 1;
        setProjectList([]);
        setProjectId();
        setProjectName();
        setCreativeTasks([]);
        fetchProjects(1).then(data => {
            if (cancelled) return;
            const items = Array.isArray(data?.items) ? data.items : [];
            setProjectList(items);
            setHasMore(items.length < (data?.total || 0));
        }).catch(err => {
            if (!cancelled) message.warning(err?.message || '加载项目失败');
        });
        return () => { cancelled = true; };
    }, [fetchProjects]);

    useEffect(() => {
        let cancelled = false;
        if (tabKey === TAB_KEY.CREATIVE || tabKey === TAB_KEY.ENTERPRISE) {
            setFavoriteList([]);
            return undefined;
        }
        API.listBookmarkTaskv2({ module: tabKey, member: getMember() }).then(res => {
            if (res?.code !== 0) throw new Error(res?.message);
            if (!cancelled) setFavoriteList(res.data.filter(item => item.results?.length > 0));
        }).catch(err => {
            if (!cancelled) message.warning(err?.message || '加载收藏失败');
        });
        return () => { cancelled = true; };
    }, [getMember, tabKey]);

    useEffect(() => {
        if (globalOrgInfo?.isPersonal && tabKey === TAB_KEY.ENTERPRISE) {
            setTabKey(TAB_KEY.MODEL);
        }
    }, [globalOrgInfo?.isPersonal, tabKey]);

    const fetchMoreData = async () => {
        const nextPage = pageIndexRef.current + 1;
        try {
            const data = await fetchProjects(nextPage);
            const items = Array.isArray(data?.items) ? data.items : [];
            pageIndexRef.current = nextPage;
            setProjectList(prev => {
                const next = prev.concat(items);
                setHasMore(next.length < (data?.total || 0));
                return next;
            });
        } catch (err) {
            message.warning(err?.message || '加载项目失败');
        }
    };

    const onToggleTask = (task) => {
        const selectionKey = getSelectionKey(task);
        if (!props.multiple) {
            setSelectedTasks([task]);
            return;
        }
        setSelectedTasks(prev => {
            if (prev.some(item => getSelectionKey(item) === selectionKey)) {
                return prev.filter(item => getSelectionKey(item) !== selectionKey);
            }
            if (props.maxCount && prev.length >= props.maxCount) {
                message.info(`最多选择${props.maxCount}张图片`);
                return prev;
            }
            return prev.concat(task);
        });
    };

    const openProject = useCallback(async (project) => {
        const nextProjectId = String(project?.id || '');
        if (!nextProjectId) {
            message.warning('项目信息异常，请刷新后重试');
            return;
        }

        setProjectId(nextProjectId);
        setProjectName(project?.projectName || project?.name || '未命名项目');
        setSelectedTasks([]);

        if (tabKey !== TAB_KEY.CREATIVE) return;

        const requestId = creativeRequestIdRef.current + 1;
        creativeRequestIdRef.current = requestId;
        setCreativeTasks([]);
        try {
            const pageData = await getFlowProjectHistoryImages({
                projectId: nextProjectId,
                pageIndex: 1,
                pageSize: 200,
            });
            if (creativeRequestIdRef.current !== requestId) return;
            setCreativeTasks(normalizeCreativeTasks(pageData?.items, nextProjectId));
        } catch (err) {
            if (creativeRequestIdRef.current !== requestId) return;
            setCreativeTasks([]);
            message.warning(err?.message || '加载项目图片失败');
        }
    }, [tabKey]);

    const firstFavorite = favoriteList[0];
    const showFavorite = projectList.length > 0 && tabKey !== TAB_KEY.CREATIVE && tabKey !== TAB_KEY.ENTERPRISE;
    const visibleProjectList = useMemo(() => (
        tabKey === TAB_KEY.CREATIVE
            ? projectList.filter((item) => Boolean(getProjectCover(item, tabKey)))
            : projectList
    ), [projectList, tabKey]);

    return (
        <div className={styles.ListContainer}>
            {projectName ? (
                <div className={styles.HeaderRow}>
                    <div className={styles.ProjectTitle}>
                        <GoBack goBack={() => { setProjectId(); setProjectName(); }} />
                        <p className={styles.Title}><span>{projectName}</span></p>
                    </div>
                    {props.multiple && <div className={styles.SelectedCount}>已选择 {selectedTasks.length}{props.maxCount ? ` / ${props.maxCount}` : ''} 张</div>}
                </div>
            ) : (
                <div className={styles.HeaderRow}>
                    <Tabs activeKey={tabKey} items={tabItems} onChange={setTabKey} />
                    {props.multiple && <div className={styles.SelectedCount}>已选择 {selectedTasks.length}{props.maxCount ? ` / ${props.maxCount}` : ''} 张</div>}
                </div>
            )}

            <div className={`${styles.ListContent} ${projectId && styles.Hidden} _beauty_scrollbar`}>
                <InfiniteScroll
                    length={projectList.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    scrollableParent={document.querySelector(`.${styles.ListContent}`)}
                    loadingComponent={<div className={styles.ScrollTips}><h4>加载中...</h4></div>}
                    endComponent={visibleProjectList.length ? <div className={styles.ScrollTips}><span>没有更多内容了</span></div> : <div className={styles.EmptyContent}><Empty /></div>}
                >
                    <div className={styles.ProjectGrid}>
                        {showFavorite ? (
                            <ProjectItem
                                key="favorite"
                                tabKey={tabKey}
                                favorite
                                mainImage={firstFavorite?.results?.[0]?.image || ''}
                                projectName="我的收藏"
                                onClick={() => { setProjectId('favorite'); setProjectName('我的收藏'); }}
                            />
                        ) : null}
                        {visibleProjectList.map(item => (
                            <ProjectItem
                                key={item.id}
                                {...item}
                                tabKey={tabKey}
                                onClick={() => openProject(item)}
                            />
                        ))}
                    </div>
                </InfiniteScroll>
            </div>

            {projectId ? (
                <TaskList
                    tabKey={tabKey}
                    member={member}
                    projectId={projectId}
                    multiple={props.multiple}
                    selectedTasks={selectedTasks}
                    creativeTasks={creativeTasks}
                    onToggleTask={onToggleTask}
                    onDoubleClick={props.onDoubleClick}
                />
            ) : null}
        </div>
    );
});

export default PickFromProject;
